import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import IconButton from '@mui/material/IconButton';
import Form from '@nineeng/forms';
import { useState, useMemo } from 'react';
import { Tabs, FormDialog } from '@repo/components';
import { EventEmitter } from '@repo/shared/helpers';
import { useBreadcrumbLevel } from '@repo/components/hooks';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { useConfig } from 'src/hooks/useConfig';
import {
	getSystemStatus,
	getConfigs,
	getCurrentConfig,
	saveConfig,
	loadConfig,
	deleteConfig,
	Config,
} from 'src/api/admin/system';

export const Route = createFileRoute('/_admin/system/overview')({
	loader: async () => {
		const [status, configs] = await Promise.all([getSystemStatus(), getConfigs()]);
		return { status, configs };
	},
	component: SystemOverviewPage,
});

function SystemOverviewPage() {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { status: initialStatus, configs: initialConfigs } = Route.useLoaderData();
	const { user: authenticatedUser } = useAuthenticatedUser();
	const { config } = useConfig();
	const isDemoEnv = config?.isDemoEnv || false;

	const { data: status } = useQuery({
		queryKey: ['system-status'],
		queryFn: () => getSystemStatus(),
		initialData: initialStatus,
	});

	const { data: configs, refetch: refetchConfigs } = useQuery({
		queryKey: ['system-configs'],
		queryFn: () => getConfigs(),
		initialData: initialConfigs,
	});

	const [isLoading, setLoading] = useState(false);
	const [isSaveConfigDialogOpen, setIsSaveConfigDialogOpen] = useState(false);
	const [configName, setConfigName] = useState('');
	const [searchQuery, setSearchQuery] = useState('');
	const [activeTab, setActiveTab] = useState(0);

	useBreadcrumbLevel(0, 'System');
	useBreadcrumbLevel(1, 'Overview');

	// Get current user's owner name (use email)
	const currentUserOwnerName = useMemo(() => {
		if (!authenticatedUser) return '';
		return authenticatedUser.email || '';
	}, [authenticatedUser]);

	// Separate configs into own, other, and NineID templates
	const { ownConfigs, otherConfigs, nineIdConfigs } = useMemo(() => {
		const own = configs.filter((c) => c.owner === currentUserOwnerName);
		const nineId = configs.filter((c) => c.owner === 'NineID');
		const other = configs.filter((c) => c.owner !== currentUserOwnerName && c.owner !== 'NineID');
		return { ownConfigs: own, otherConfigs: other, nineIdConfigs: nineId };
	}, [configs, currentUserOwnerName]);

	// Filter configs based on search query and active tab
	const filteredConfigs = useMemo(() => {
		let configsToFilter: Config[];
		if (activeTab === 0) {
			configsToFilter = ownConfigs;
		} else if (activeTab === 1) {
			configsToFilter = otherConfigs;
		} else {
			configsToFilter = nineIdConfigs;
		}

		if (!searchQuery.trim()) {
			return configsToFilter;
		}
		const query = searchQuery.toLowerCase();
		return configsToFilter.filter(
			(c) =>
				c.name.toLowerCase().includes(query) ||
				c.owner.toLowerCase().includes(query) ||
				c.configId.toLowerCase().includes(query),
		);
	}, [ownConfigs, otherConfigs, nineIdConfigs, searchQuery, activeTab]);

	const handleLoadConfig = async (configId: string) => {
		if (!isDemoEnv) {
			EventEmitter.emit('toast', {
				type: 'error',
				message: 'Loading configurations is only allowed in demo environments',
			});
			return;
		}

		if (!confirm('Are you sure you want to load this configuration? This will replace all current system data and is not reversible!')) {
			return;
		}

		setLoading(true);
		try {
			await loadConfig(configId);
			EventEmitter.emit('toast', { type: 'success', message: 'Configuration loaded successfully! Redirecting...' });
			setTimeout(() => {
				navigate({ to: '/authentication/logout' });
			}, 1500);
		} catch (error) {
			console.error('Error loading config:', error);
			EventEmitter.emit('toast', { type: 'error', message: 'Failed to load configuration' });
		}
		setLoading(false);
	};

	const handleDeleteConfig = async (configId: string, event: React.MouseEvent) => {
		event.stopPropagation();

		if (!confirm('Are you sure you want to delete this configuration template? This action cannot be undone.')) {
			return;
		}

		setLoading(true);
		try {
			await deleteConfig(configId);
			EventEmitter.emit('toast', { type: 'success', message: 'Config deleted successfully!' });
			refetchConfigs();
		} catch (error: any) {
			console.error('Error deleting config:', error);
			const errorMessage = error.body?.error || error.body?.message || 'Failed to delete config';
			EventEmitter.emit('toast', { type: 'error', message: errorMessage });
		}
		setLoading(false);
	};

	const handleSaveConfig = async () => {
		if (!configName.trim()) {
			EventEmitter.emit('toast', { type: 'error', message: 'Config name is required' });
			return;
		}

		if (!authenticatedUser) {
			EventEmitter.emit('toast', { type: 'error', message: 'User not authenticated' });
			return;
		}

		setLoading(true);
		try {
			const configDataResponse = await getCurrentConfig();
			const ownerName = authenticatedUser.email || 'Unknown';

			const response = await saveConfig({
				name: configName.trim(),
				owner: ownerName,
				config: configDataResponse?.settings || {},
			});

			if (response) {
				EventEmitter.emit('toast', { type: 'success', message: 'Config saved successfully!' });
				setIsSaveConfigDialogOpen(false);
				setConfigName('');
				refetchConfigs();
			} else {
				EventEmitter.emit('toast', { type: 'error', message: 'Failed to save config' });
			}
		} catch (error) {
			console.error('Error saving config:', error);
			EventEmitter.emit('toast', { type: 'error', message: 'Failed to save config' });
		}
		setLoading(false);
	};

	const statusForm = {
		sections: [
			{
				type: 'SettingsSection',
				labelWidth: '70%',
				fields: [
					{
						rowLabel: 'Version',
						type: 'Typography',
						children: status?.version,
						sx: { marginTop: 1 },
					},
				],
			},
		],
	};

	return (
		<div className="p-4">
			<div className="flex gap-2 flex-row flex-wrap">
				<div className="flex flex-col gap-2 w-1/3 flex-1 min-w-96">
					<Paper variant="outlined" className="p-4 h-40">
						<Typography variant="h6">System</Typography>
						<Form schema={statusForm} originalValues={status} />
					</Paper>

					<Paper variant="outlined" className="p-4 h-40">
						<Typography variant="h6" sx={{ marginBottom: 2 }}>
							Configuration Templates
						</Typography>
						<Button
							variant="contained"
							onClick={() => setIsSaveConfigDialogOpen(true)}
							disabled={isLoading}
							fullWidth
						>
							Save Current Config
						</Button>
					</Paper>
				</div>

				{isDemoEnv && (
					<Paper variant="outlined" className="p-4 w-full flex-1 min-w-96">
						<Typography variant="h6" sx={{ marginBottom: 2 }}>
							Available Configurations
						</Typography>
						<div className="mt-4 mb-4">
							<TextField
								fullWidth
								placeholder="Search configs by name, owner, or ID..."
								value={searchQuery}
								onChange={(e) => setSearchQuery(e.target.value)}
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											<SearchIcon />
										</InputAdornment>
									),
								}}
								variant="outlined"
								size="small"
							/>
						</div>
						<Tabs value={activeTab} onChange={(value) => setActiveTab(value)}>
							<Tabs.TabList>
								<Tabs.Tab value={0} label={`My Templates (${ownConfigs.length})`} />
								<Tabs.Tab value={1} label={`Other Templates (${otherConfigs.length})`} />
								<Tabs.Tab value={2} label={`NineID Templates (${nineIdConfigs.length})`} />
							</Tabs.TabList>
							<Tabs.TabPanel value={0} selectedValue={activeTab} className="!mt-0 !p-0">
								{filteredConfigs && filteredConfigs.length > 0 ? (
									<List>
										{filteredConfigs.map((c) => (
											<ListItem
												key={c.configId}
												divider
												onClick={() => handleLoadConfig(c.configId)}
												secondaryAction={
													<IconButton
														edge="end"
														aria-label="delete"
														onClick={(e) => handleDeleteConfig(c.configId, e)}
														disabled={isLoading}
														color="error"
													>
														<DeleteIcon />
													</IconButton>
												}
												sx={{
													width: '100%',
													'&:hover': {
														backgroundColor: 'action.hover',
														cursor: 'pointer',
													},
												}}
											>
												<ListItemText
													primary={c.name}
													secondary={`Owner: ${c.owner} • Created: ${new Date(c.createdAt).toLocaleDateString()}`}
												/>
											</ListItem>
										))}
									</List>
								) : (
									<Typography variant="body2" color="text.secondary" sx={{ padding: 2, textAlign: 'center' }}>
										{searchQuery.trim() ? 'No configs found matching your search' : 'No config templates available'}
									</Typography>
								)}
							</Tabs.TabPanel>
							<Tabs.TabPanel value={1} selectedValue={activeTab} className="!mt-0 !p-0">
								{filteredConfigs && filteredConfigs.length > 0 ? (
									<List>
										{filteredConfigs.map((c) => (
											<ListItem
												key={c.configId}
												divider
												onClick={() => handleLoadConfig(c.configId)}
												sx={{
													width: '100%',
													'&:hover': {
														backgroundColor: 'action.hover',
														cursor: 'pointer',
													},
												}}
											>
												<ListItemText
													primary={c.name}
													secondary={`Owner: ${c.owner} • Created: ${new Date(c.createdAt).toLocaleDateString()}`}
												/>
											</ListItem>
										))}
									</List>
								) : (
									<Typography variant="body2" color="text.secondary" sx={{ padding: 2, textAlign: 'center' }}>
										{searchQuery.trim() ? 'No configs found matching your search' : 'No config templates available'}
									</Typography>
								)}
							</Tabs.TabPanel>
							<Tabs.TabPanel value={2} selectedValue={activeTab} className="!mt-0 !p-0">
								{filteredConfigs && filteredConfigs.length > 0 ? (
									<List>
										{filteredConfigs.map((c) => (
											<ListItem
												key={c.configId}
												divider
												onClick={() => handleLoadConfig(c.configId)}
												sx={{
													width: '100%',
													'&:hover': {
														backgroundColor: 'action.hover',
														cursor: 'pointer',
													},
												}}
											>
												<ListItemText
													primary={c.name}
													secondary={`Owner: ${c.owner} • Created: ${new Date(c.createdAt).toLocaleDateString()}`}
												/>
											</ListItem>
										))}
									</List>
								) : (
									<Typography variant="body2" color="text.secondary" sx={{ padding: 2, textAlign: 'center' }}>
										{searchQuery.trim() ? 'No configs found matching your search' : 'No config templates available'}
									</Typography>
								)}
							</Tabs.TabPanel>
						</Tabs>
					</Paper>
				)}
			</div>

			<FormDialog
				isOpen={isSaveConfigDialogOpen}
				onClose={() => {
					setIsSaveConfigDialogOpen(false);
					setConfigName('');
				}}
				title="Save Configuration Template"
				description="Enter a name for this configuration template. The current system configuration will be saved."
				onSubmit={handleSaveConfig}
				disabled={!configName.trim() || isLoading}
				saveLabel="Save"
			>
				<TextField
					label="Configuration Name"
					value={configName}
					onChange={(e) => setConfigName(e.target.value)}
					fullWidth
					required
					autoFocus
					placeholder="e.g., Production Config, Demo Setup, etc."
				/>
			</FormDialog>
		</div>
	);
}
